import { ThunkResult } from "src/shared/types";
import { createReduxOperation, createActionWithPayload, makeConstants } from "src/store/utils";
import CategoriesModel from "src/shared/models/CategoriesModel";
import AllPinsModel from "src/shared/models/AllPinsModel";
import { PageTypes } from "src/shared/models/MetricOperations";
import { getCharts } from "src/api/common";
import { getTimeFromTimestamp, safeGet, TimeDifferenceGA } from "src/shared/utils";
import * as api from "src/api/category";
import { FetchCategoryPayload, SearchCategoryPayload } from "./types";

// generate constants, actions and reducers
const {
  actions: [categoryRequest, categorySuccess, categoryFailure, categoryReset],
  reducer: categoryReducer,
} = createReduxOperation("CATEGORY");

const {
  actions: [searchRequest, searchSuccess, searchFailure, searchReset],
  reducer: searchReducer,
} = createReduxOperation("SEARCH_CATEGORY");

export const parentReducerConstants = makeConstants([
  "SET_CATEGORIES_FILTERS",
  "SET_HEADER_L2",
  "FETCHING_CATEGORY_DATA",
]);

export const setCategoriesFilters = createActionWithPayload<symbol, IObject<any>>(
  parentReducerConstants.get("SET_CATEGORIES_FILTERS") as symbol
);

export const toggleChartsDataPending = createActionWithPayload<symbol, boolean>(
  parentReducerConstants.get("FETCHING_CATEGORY_DATA") as symbol
);

export const setHeader = createActionWithPayload<symbol, string>(parentReducerConstants.get("SET_HEADER_L2") as symbol);

// handle thunk functions
export function fetchCategory({
  category,
  fresh: shouldFetchFreshData,
  filters,
}: FetchCategoryPayload): ThunkResult<Promise<void>> {
  return async (dispatch, getState) => {
    const {
      app: { selectedTab: timeTab },
    } = getState();

    dispatch(categoryRequest());
    dispatch(toggleChartsDataPending(true));
    const { response: categoryResponse, error: categoryError } = await api.getCategories({
      category,
      timeTab,
      shouldFetchFreshData,
    });
    if (categoryResponse) {
      const allCategories = CategoriesModel.createMetric(categoryResponse);
      allCategories["lastUpdatedTimeStamp"] = safeGet(
        getState(),
        "category.metrics.payload.lastUpdatedTimeStamp",
        null
      );

      dispatch(categorySuccess(allCategories));

      const filterConfig = safeGet(getState(), "app.appConfig.payload", null);
      const userRoles = getState().app.userRoles.payload;
      let newFilters = filters;

      if (!userRoles.hasFullAccess && safeGet(filters, "city", []).length === 0) {
        newFilters = { ...filters, city: userRoles.cities };
      }

      const bulkChartRequest = CategoriesModel.getCategoryBulkChartRequest(
        timeTab,
        allCategories,
        newFilters,
        filterConfig
      );
      const { response: chartsResponse, error: chartsError } = await getCharts({
        data: bulkChartRequest,
        timeTab,
        shouldFetchFreshData,
      });
      if (chartsResponse && chartsResponse.data) {
        const formattedChart = CategoriesModel.formatCharts(chartsResponse.data);
        const categoriesWithCharts = CategoriesModel.combineCategoryChart(formattedChart, allCategories);
        const withData = CategoriesModel.calculateEachChart(categoriesWithCharts, timeTab);
        withData.lastUpdatedTimeStamp = getTimeFromTimestamp(chartsResponse.lastUpdatedTimeStamp);
        // Prevent some earlier tab data from replacing redux store value of new tab data due to slow response (Race condition)
        if (getState().app.selectedTab !== timeTab) {
          dispatch(toggleChartsDataPending(false));
          return;
        }
        dispatch(categorySuccess(withData));
        if (timeTab == "Today") {
          const currentChartsData = chartsResponse.data.filter((item) => {
            return item["time_view"] == "currentChart";
          });
          TimeDifferenceGA(currentChartsData, "Category Page");
        }
      } else {
        dispatch(categoryFailure(chartsError));
      }
    } else {
      dispatch(categoryFailure(categoryError));
    }
    dispatch(toggleChartsDataPending(false));
  };
}

export function searchCategory({ text, superCategory }: SearchCategoryPayload): ThunkResult<Promise<void>> {
  return async (dispatch, getState) => {
    dispatch(searchRequest());
    if (text.length === 0) {
      dispatch(searchSuccess([]));
      return;
    }
    const timeTab = getState().app.selectedTab;
    const { response, error } = await api.searchCategories({ timeTab, text, superCategory });
    if (response) {
      const metrics = CategoriesModel.createMetric(response);
      const ungroupedMetrics = CategoriesModel.mergeMetrices(metrics);
      dispatch(searchSuccess(ungroupedMetrics));
      const filterConfig = getState().app.appConfig.payload;

      const bulkChartRequest = AllPinsModel.getBulkChartRequest(
        timeTab,
        ungroupedMetrics,
        PageTypes.L2Search,
        "metric",
        undefined,
        filterConfig
      );

      const { response: chartsResponse } = await getCharts({
        data: bulkChartRequest,
        timeTab,
      });
      if (chartsResponse && chartsResponse.data) {
        const formattedChart = AllPinsModel.formatCharts(chartsResponse.data);
        const fetchedPins = AllPinsModel.combineMetricsAndCharts(
          formattedChart,
          { data: ungroupedMetrics },
          PageTypes.L2Search
        );
        const fetchedPinsWithData = AllPinsModel.calculationsFromCharts({ data: fetchedPins }, timeTab);
        dispatch(searchSuccess(fetchedPinsWithData.data));
        if (timeTab == "Today") {
          const currentChartsData = chartsResponse.data.filter((item) => {
            return item["time_view"] == "currentChart";
          });
          TimeDifferenceGA(currentChartsData, "Search Category Page");
        }
      }
    } else {
      dispatch(searchFailure(error));
    }
  };
}

export { categoryReducer, categoryReset, searchReducer, searchReset };
